// ============================================
// Button Component
// Design System v2.0 - Gradient primary + refined shadows
// ============================================

import { m } from 'framer-motion';
import type { ComponentChildren } from 'preact';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  children: ComponentChildren;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  ariaLabel?: string;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  type = 'button',
  className = '',
  ariaLabel
}: ButtonProps) {
  const baseClasses = `
    inline-flex items-center justify-center gap-2
    font-semibold transition-all
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variantClasses: Record<ButtonVariant, string> = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 rounded-lg',
    danger: 'bg-gradient-to-br from-red-500 to-red-600 text-white rounded-lg shadow-1 hover:shadow-2'
  };

  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-5 py-2.5 text-base',
    lg: 'px-7 py-3.5 text-lg'
  };

  return (
    <m.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      whileHover={!disabled ? { scale: 1.02 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      aria-label={ariaLabel}
      aria-busy={loading}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </m.button>
  );
}

// ============================================
// Icon Button
// ============================================

interface IconButtonProps {
  icon: ComponentChildren;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel: string;
  size?: ButtonSize;
  variant?: 'default' | 'ghost';
  className?: string;
}

export function IconButton({
  icon,
  onClick,
  disabled = false,
  ariaLabel,
  size = 'md',
  variant = 'default',
  className = ''
}: IconButtonProps) {
  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  };

  const variantClasses = variant === 'ghost'
    ? 'bg-transparent hover:bg-slate-100'
    : 'audio-button';

  return (
    <m.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex items-center justify-center
        rounded-full transition-all
        focus:outline-none focus-visible:shadow-glow-primary
        disabled:opacity-50 disabled:cursor-not-allowed
        ${sizeClasses[size]}
        ${variantClasses}
        ${className}
      `}
      whileHover={!disabled ? { scale: 1.1 } : undefined}
      whileTap={!disabled ? { scale: 0.9 } : undefined}
      aria-label={ariaLabel}
    >
      {icon}
    </m.button>
  );
}
